import React, { useState, useEffect } from "react";
import { getS3DownloadUrl } from "../services/s3StorageService";

/**
 * Reusable component for rendering project and sprite thumbnails from S3 paths.
 * Resolves temporary signed GET URLs on demand with in-memory caching.
 */
const S3ThumbnailImage = ({
  thumbnailPath,
  fallback = null,
  alt = "Thumbnail",
  className = "w-10 h-10 rounded-lg overflow-hidden border border-slate-200 dark:border-white/10 flex-shrink-0 bg-black/10 flex items-center justify-center",
  imgClassName = "w-full h-full object-cover",
}) => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    if (!thumbnailPath) {
      setUrl("");
      setLoading(false);
      setHasError(false);
      return;
    }

    // Direct data URI or full URL
    if (
      thumbnailPath.startsWith("data:") ||
      thumbnailPath.startsWith("http://") ||
      thumbnailPath.startsWith("https://")
    ) {
      setUrl(thumbnailPath);
      setLoading(false);
      setHasError(false);
      return;
    }

    setLoading(true);
    setHasError(false);

    getS3DownloadUrl(thumbnailPath)
      .then((downloadUrl) => {
        if (!isCancelled) {
          setUrl(downloadUrl);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.warn(`Failed to resolve thumbnail URL for ${thumbnailPath}:`, err);
        if (!isCancelled) {
          setHasError(true);
          setLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [thumbnailPath]);

  if (!thumbnailPath || hasError || (!url && !loading)) {
    return fallback ? <>{fallback}</> : null;
  }

  return (
    <div className={className}>
      {url ? (
        <img
          src={url}
          alt={alt}
          className={`${imgClassName} ${loading ? "opacity-0" : "opacity-100"} transition-opacity duration-200`}
          onError={() => setHasError(true)}
          loading="lazy"
        />
      ) : (
        fallback
      )}
    </div>
  );
};

export default S3ThumbnailImage;
